import { readJSONFileToObject } from "../helper/files"
import { mergeDeep, findDeep } from "../helper/objects"

/**
 * Class TranslationManager.js
 *
 * @class TranslationManager
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.11] app/src/manager/TranslationManager.js
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export class TranslationManager {
  constructor(
    store,
    fnGetAuthToken
  ) {
    /** @type Vuex */
    this._store = store

    this._fnGetAuthToken = fnGetAuthToken

    this._fallbackLanguage = 'en'

    this._catalogs = {};
    this._defaultCatalog = null;
    this._loadedCatalogs = {};
    this._loadingCatalogs = {};

    this._paramPlaceholders = [
        (par) => `{{${par}}}`,
        (par) => `{${par}}`
    ]
  }

  defineCatalog(catalogName, fileLocations){
    this._catalogs[catalogName] = {
      fileLocations : fileLocations,
      name : catalogName,
      loadedLanguages : {},
    }

    if (this._defaultCatalog === null)
      this.defaultCatalog(catalogName)

    return this
  }

  clearLoadedLanguages(){
    this._loadedCatalogs = {};
    this._loadingCatalogs = {};

    for (let catalogName in this._catalogs) {
      this._catalogs[catalogName].loadedLanguages = {};
    }

    return this
  }

  defaultCatalog(catalogName){
    this._defaultCatalog = catalogName
    return this
  }

  isCatalogLoading(catalogName, language){
    return this._loadingCatalogs?.[catalogName]?.[language] > 0;
  }

  isCatalogLoaded(catalogName, language){
    return this._loadedCatalogs?.[catalogName]?.[language] > 0;
  }

  loadLanguages(
      languages = ['en'],
      catalogsToLoad = [],
      evenIfAlreadyLoading = false
  ) {
    languages = languages.filter(onlyUnique)

    const loadActions = [];

    if (!catalogsToLoad.length){
      catalogsToLoad = Object.keys(this._catalogs);
    }

    for (let i=0; i<languages.length; i++) {
       const language = languages[i];

      for (let j=0; j<catalogsToLoad.length; j++) {
        const catalogName = catalogsToLoad[j];
        const catalog = this._catalogs[catalogName]

        if (
            (evenIfAlreadyLoading || !this.isCatalogLoading(catalogName, language)) &&
            !this.isCatalogLoaded(catalogName, language)
        ) {
          languages.forEach((language) => {
            setCatalogLanguageLoading.call(this, catalogName, language, true);
            loadActions.push({catalog, language})
          });
        }
      }
    }

    const self = this;

    return Promise.all(loadActions.map((loadAction) => {
      return new Promise(function (res, rej) {
        const {catalog, language} = loadAction;

        let token = self._fnGetAuthToken();

        const headers = {
          authorization: token ? `Bearer ${token}` : "",
        }

        let readPromises = []

        for (let i=0; i<catalog.fileLocations.length; i++){
          const fileLocation = catalog.fileLocations[i];
          const filePath = `${fileLocation}/${catalog.name}.${language}.json`
          readPromises.push(readJSONFileToObject(filePath, true, headers))
        }

        return Promise.all(readPromises).then(resolvedValues => {
          // check if all files were loaded correctly  (readJSONFileToObject did not return null, for any of the load actions)
          const allFilesCorrectlyLoaded = !resolvedValues.some(el => el === null);

          if (allFilesCorrectlyLoaded){
            let languageObject = resolvedValues[0];

            // merge secondary locations into primary
            for (let j=1; j<resolvedValues.length; j++){
              languageObject = mergeDeep(languageObject, resolvedValues[j]);
            }

            catalog.loadedLanguages[language] = languageObject;
            // Set catalog as loaded
            setCatalogLanguageLoaded.call(self, catalog.name, language, true);
            res(languageObject)
          }else{
            res(null)
          }

          // Set catalog as no longer loading
          setCatalogLanguageLoading.call(self, catalog.name, language, false);
        })
      })
    }))
  }

  translationStartsWithParam(key, paramKey, options = null) {
    options = {
      ...(options || {}),
    };
    options.params = null;

    const translation = this.translate(key, options);

    if (typeof translation !== 'string') {
      return false;
    }

    const transTrim = translation.trimStart();

    return this._paramPlaceholders.some((placeholderFactory) => {
      return transTrim.startsWith(placeholderFactory(paramKey));
    });
  }

  translate(key, options = null) {
    let _options = {
      language: this.getCurrentLanguage(),
      fallbackLanguage: this._fallbackLanguage
    }

    if (key !== null && options === null && typeof key === 'object'){
      options = key;
      key = null;
    }

    if (!key && options?.key) {
      key = options?.key;
    }

    const catalogKey = typeof options === 'string' ? options : (options && options.catalog ? options.catalog : this._defaultCatalog)

    if (!catalogKey) throw new Error('No catalog was defined')
    if (!this._catalogs[catalogKey]) throw new Error(`Unknown catalog: '${catalogKey}'`)

    if (options !== null) Object.assign(_options, options)

    return translatePrivate.call(this,
      key,
      this._catalogs[catalogKey],
      _options.language,
      _options,
      !_options.fallbackLanguage || _options.language === _options.fallbackLanguage
    )
  }

  t(key, options = null) {
    return this.translate(key, options)
  }

  translationExists(key, catalogKey, language) {
    if (!language) {
      language = this.getCurrentLanguage();
    }

    if (this._catalogs[catalogKey]){
      return !!findDeep(this._catalogs[catalogKey].loadedLanguages[language], key)
    }

    return false;
  }

  getCurrentLanguage(){
    return this._store.state.currentLanguage || 'en';
  }

  setCurrentLanguage(language) {
    this._store.dispatch('setCurrentLanguageAndReload', language)
  }
}

function setCatalogLanguageLoading(catalogName, language, loading)
{
  if (!this._loadingCatalogs[catalogName]){
    this._loadingCatalogs[catalogName] = {};
  }

  const loadingVal = this._loadingCatalogs?.[catalogName]?.[language] || 0;
  this._loadingCatalogs[catalogName][language] = loadingVal + (loading ? 1 : -1);
  //
  // if (loading){
  //   console.log(`loading catalog ${catalogName} with language '${language}'`)
  // }
}

function setCatalogLanguageLoaded(catalogName, language, loaded)
{
  if (!this._loadedCatalogs[catalogName]){
    this._loadedCatalogs[catalogName] = {};
  }

  const loadingVal = this._loadedCatalogs?.[catalogName]?.[language] || 0;
  this._loadedCatalogs[catalogName][language] = loadingVal + (loaded ? 1 : -1);
  //
  // if (loaded){
  //   console.log(`loaded catalog ${catalogName} with language '${language}'`)
  // }
}

function translatePrivate(key, catalog, language, options = null, fallbackUsed = false){
  if (!Object.prototype.hasOwnProperty.call(catalog.loadedLanguages, language))
    return fallbackUsed ? key : translatePrivate.call(this, key, catalog, options.fallbackLanguage, options, true)

  let translationFind = findDeep(catalog.loadedLanguages[language], key)

  if (typeof translationFind === 'undefined')
    return fallbackUsed ? key : translatePrivate.call(this, key, catalog, options.fallbackLanguage, options, true)

  if (options.params){
    for (let param in options.params){
      for (let i=0; i<this._paramPlaceholders.length; i++){
        const
            paramPlaceholderReplacer = this._paramPlaceholders[i] ,
            toBeReplaced = paramPlaceholderReplacer(param)
        ;

        if (translationFind.includes(toBeReplaced)){
          let valueToInsert = options.params[param];

          // Apply optional transform only when this parameter starts the translation.
          if (
              typeof options.startingParamTransform === 'function' &&
              translationFind.trimStart().startsWith(toBeReplaced)
          ) {
            valueToInsert = options.startingParamTransform(valueToInsert, param, options.params);
          }

          translationFind = translationFind.replace(paramPlaceholderReplacer(param), valueToInsert)
          break;
        }
      }
    }
  }

  return translationFind
}

function onlyUnique(value, index, self) {
  return self.indexOf(value) === index
}